import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import MemberTimeline from '../t2m/MemberTimeline';

// ─── Status badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = (status || '').toLowerCase();
  const color = s === 'active' ? '#22c55e'
    : s === 'frozen' ? '#3b82f6'
    : s === 'cancelled' ? '#ef4444'
    : '#6b7280';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
      background: color + '22', color, border: `1px solid ${color}44`,
      textTransform: 'uppercase', letterSpacing: '0.04em',
    }}>
      {status || 'Unknown'}
    </span>
  );
}

// ─── Module badge ─────────────────────────────────────────────────────────────
function ModuleBadge({ module }) {
  const cfg = {
    lead:         { label: 'LE',      color: '#f59e0b' },
    t2m:          { label: 'T2M',     color: '#22c55e' },
    pb101:        { label: 'PB101',   color: '#3b82f6' },
    inbound_lead: { label: 'INBOUND', color: '#a855f7' },
  }[module] || { label: module?.toUpperCase() || '?', color: '#6b7280' };
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
      background: cfg.color + '22', color: cfg.color, border: `1px solid ${cfg.color}44`,
    }}>
      {cfg.label}
    </span>
  );
}

// ─── Billing display helper ────────────────────────────────────────────────────
// Shows the real billing_amount + billing_period as captured from list_
// (Sprint 20). Deliberately NOT converted to a monthly-equivalent here — showing
// staff the actual real recurring charge (e.g. "$1,352.04/yr") is more useful
// and less confusing than a computed figure that doesn't match what PbP/the
// member actually sees on their statement. $0 is shown as-is, not hidden —
// per BK: $0 is often legitimate (Welld-covered base membership), not a gap.
function formatBilling(amount, period) {
  if (amount == null || !period) return null;
  const periodLabel = { monthly: '/mo', yearly: '/yr', 'every 3 months': '/qtr', daily: '/day' }[period] || `/${period}`;
  return `$${Number(amount).toFixed(2)}${periodLabel}`;
}

// ─── Member card ──────────────────────────────────────────────────────────────
function MemberCard({ member, onSelect, selected }) {
  const lastContact = member.last_contact_at
    ? new Date(member.last_contact_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <div
      onClick={() => onSelect(member)}
      style={{
        background: selected ? 'var(--surface-3)' : 'var(--surface-2)',
        border: `1px solid ${selected ? '#facc15' : 'var(--border)'}`,
        borderRadius: 8, padding: '12px 16px', cursor: 'pointer',
        marginBottom: 8, transition: 'border-color 0.15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', textTransform: 'capitalize' }}>
              {member.full_name || `${member.first_name || ''} ${member.last_initial || ''}`.trim() || '—'}
            </span>
            <StatusBadge status={member.status} />
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', background: 'var(--surface-3)', padding: '2px 6px', borderRadius: 4 }}>
              {member.location_id}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: member.contact_modules?.length ? 4 : 0 }}>
            {member.email}
            {member.phone && <span> · {member.phone}</span>}
            {member.membership_type && <span> · {member.membership_type}</span>}
            {formatBilling(member.billing_amount, member.billing_period) && (
              <span> · {formatBilling(member.billing_amount, member.billing_period)}</span>
            )}
          </div>
          {member.contact_modules?.length > 0 && (
            <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
              {member.contact_modules.map(m => <ModuleBadge key={m} module={m} />)}
              {lastContact && (
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                  Last contact: {lastContact}
                </span>
              )}
            </div>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, textAlign: 'right' }}>
          {member.contact_count > 0 && (
            <div style={{ fontWeight: 700, color: 'var(--text)', fontSize: 13 }}>{member.contact_count}</div>
          )}
          {member.contact_count > 0 && <div>contacts</div>}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function MemberSearchModule() {
  const { get } = useApi();
  const { role } = useAuth();
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [searched, setSearched]   = useState(false);
  const [selected, setSelected]   = useState(null);
  const [error, setError]         = useState(null);
  const debounceRef = useRef(null);
  const [urlParams] = useSearchParams();

  const search = useCallback((q) => {
    if (q.trim().length < 2) { setResults([]); setSearched(false); return; }
    setLoading(true);
    setError(null);
    get(`/member/search?q=${encodeURIComponent(q.trim())}`)
      .then(d => { setResults(d.members || []); setSearched(true); })
      .catch(e => { setError(e.message || 'Search failed'); setSearched(true); })
      .finally(() => setLoading(false));
  }, [get]); // eslint-disable-line react-hooks/exhaustive-deps

  // Arriving from a Lead Engine "search everyone" link (?q=...): run that search right away.
  useEffect(() => {
    const q = urlParams.get('q');
    if (q && q.trim().length >= 2) { setQuery(q); search(q); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleInput(e) {
    const val = e.target.value;
    setQuery(val);
    setSelected(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 350);
  }

  function handleSelect(member) {
    setSelected(prev => prev?.member_id === member.member_id ? null : member);
  }

  return (
    <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 120px)', overflow: 'hidden' }}>

      {/* Left panel — search + results */}
      <div style={{
        width: selected ? 380 : '100%', flexShrink: 0,
        display: 'flex', flexDirection: 'column',
        borderRight: selected ? '1px solid var(--border)' : 'none',
        transition: 'width 0.2s',
      }}>
        {/* Header */}
        <div style={{ padding: '0 0 16px' }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>
            🔍 Member Search
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Search by name, email, or phone · Full contact history across all modules
          </div>
        </div>

        {/* Search input */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <input
            type="text"
            value={query}
            onChange={handleInput}
            placeholder="Name, email, or phone..."
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box',
              background: 'var(--surface-2)', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--text)', padding: '10px 40px 10px 14px',
              fontSize: 14, outline: 'none', fontFamily: 'inherit',
            }}
          />
          {loading && (
            <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>
              <div className="spinner" style={{ width: 16, height: 16 }} />
            </div>
          )}
          {!loading && query && (
            <button
              onClick={() => { setQuery(''); setResults([]); setSearched(false); setSelected(null); }}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 16 }}
            >✕</button>
          )}
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {error && (
            <div style={{ color: '#ef4444', fontSize: 13, padding: '8px 0' }}>{error}</div>
          )}

          {!loading && searched && results.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">🔍</div>
              <div className="empty-state-text">No members found for "{query}"</div>
            </div>
          )}

          {!searched && !loading && (
            <div className="empty-state">
              <div className="empty-state-icon">🔍</div>
              <div className="empty-state-text">Type at least 2 characters to search</div>
            </div>
          )}

          {results.map(member => (
            <MemberCard
              key={member.member_id + member.location_id}
              member={member}
              onSelect={handleSelect}
              selected={selected?.member_id === member.member_id && selected?.location_id === member.location_id}
            />
          ))}

          {searched && results.length > 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', padding: '8px 0' }}>
              {results.length} result{results.length !== 1 ? 's' : ''}
              {results.length === 50 ? ' · Showing first 50 — refine your search' : ''}
            </div>
          )}
        </div>
      </div>

      {/* Right panel — timeline */}
      {selected && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 0 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)', textTransform: 'capitalize' }}>
                {selected.full_name || selected.email}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {selected.email}
                {selected.phone && <span> · {selected.phone}</span>}
                {' · '}{selected.location_id}
                {selected.membership_type && <span> · {selected.membership_type}</span>}
                {formatBilling(selected.billing_amount, selected.billing_period) && (
                  <span> · {formatBilling(selected.billing_amount, selected.billing_period)}</span>
                )}
              </div>
            </div>
            <button
              onClick={() => setSelected(null)}
              style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12 }}
            >
              ✕ Close
            </button>
          </div>

          <MemberTimeline
            memberId={selected.member_id}
            memberName={selected.full_name}
            maxHeight="calc(100vh - 220px)"
          />
        </div>
      )}
    </div>
  );
}
